import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import sanitize from 'sanitize-html'

import { IForm } from 'core-components/classes/models/IForm'
import { Flex } from 'core-components/library/flex'
import { Table } from 'core-components/library/list'
import styles from 'core-components/library/list/Table.module.scss'
import { ICellAttrs, IColumn, ITableProps } from 'core-components/library/list/types'
import { Tooltip } from 'core-components/library/tooltip'
import { IProperties } from 'core-components/sitebuilder/propertygenerator'
import { IEditorTableProps } from 'core-components/sitebuilder/propertygenerator/adapter/Table'
import { IColumnExtended } from 'core-components/sitebuilder/propertygenerator/models/ITableExtendedProps'
import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'

import { EventBusController } from '../EventBusController'

import { CreateItemContentButton } from './table/CreateItemContentButton'
import { DeleteItemContentModal } from './table/DeleteItemContentModal'
import { EditItemContentModal } from './table/EditItemContentModal'
import { TableEvents, tableEvents } from './table/eventBusData'
import { checkRoles } from './table/helpers/checkRoles'
import { useRowActions } from './table/hooks/useRowActions'

import './table/TableClasses.scss'

interface ITableEditor {
  props: ITableProps & IEditorTableProps
  eventBusData?: IProperties
}

export const TableEditor = ({ props, eventBusData }: ITableEditor) => {
  const [tableUpd, setTableUpdate] = useState<number>(0)
  const [editItem, setEditItem] = useState<IForm | null>(null)
  const [removeItem, setRemoveItem] = useState<IForm | null>(null)
  const { rowActions, canEdit, canDelete } = useRowActions({
    rowActions: props.rowActions,
    editRoles: props.editRoles,
    deleteRoles: props.deleteRoles,
    setEditItem,
    setRemoveItem,
  })

  const user = useSelector(selectUser)
  const canCreate = useMemo(() => {
    return checkRoles(user.roleIds, props.createRoles)
  }, [props.createRoles, user?.roleIds])

  const rerender = useCallback(() => {
    setTableUpdate((last) => last + 1)
  }, [])

  const allowColumns = useMemo(() => {
    return props.columns.filter((column) => {
      const allowRoles = column['columnVisibilityRoles'] || []
      if (allowRoles.length === 0) {
        return true
      }
      return checkRoles(user.roleIds, allowRoles)
    })
  }, [props.columns, user.roleIds])

  const cellTemplate = useCallback(
    (item: IForm, column: IColumn<IForm>, cellAttrs: ICellAttrs, rowAttrs: unknown) => {
      const isRawTextColumn = (column['renderType'] as IColumnExtended['renderType']) === 'rawText'

      return (
        <td
          dangerouslySetInnerHTML={
            isRawTextColumn
              ? {
                  __html: sanitize(item[column.field].replaceAll('\n', '<br>')),
                }
              : undefined
          }
          {...cellAttrs}
          className={classNames(styles.cell, cellAttrs.className, column.className)}
          key={cellAttrs.key}
        >
          {isRawTextColumn ? null : column.renderFunction ? (
            column.renderFunction(item, column, rowAttrs)
          ) : !column.width ? (
            item[column.field]
          ) : (
            <Tooltip
              tipContent={() => <>{item['get'] ? item['get'](column.field) : item[column.field]}</>}
            >
              <div className={styles.ellipsis}>
                {item['get'] ? item['get'](column.field) : item[column.field]}
              </div>
            </Tooltip>
          )}
        </td>
      )
    },
    []
  )

  useEffect(() => {
    if (props.initializeWidgetData) {
      props.initializeWidgetData({
        actions: {
          rerender,
        },
      })
    }
  }, [props, rerender])

  useEffect(() => {
    if (eventBusData?.value) {
      const eventBus = EventBusController.getEventBus(`${eventBusData?.value}`, tableEvents)
      return eventBus.subscribe(TableEvents.filterChanged, (event, filter) => {
        props.source.setFilter(filter)
      })
    }
    return () => undefined
  }, [eventBusData, props.source])

  return (
    <Flex flexDirection={'column'}>
      {canCreate ? (
        <div>
          <CreateItemContentButton source={props.source} columns={allowColumns} />
        </div>
      ) : null}
      <Table<IForm>
        sourceUpdateCount={tableUpd}
        {...props}
        columns={allowColumns}
        rowActions={rowActions}
        cellTpl={cellTemplate}
      />
      {canEdit && editItem ? (
        <EditItemContentModal
          item={editItem}
          columns={allowColumns}
          source={props.source}
          onClose={() => setEditItem(null)}
        />
      ) : null}
      {canDelete && removeItem ? (
        <DeleteItemContentModal
          item={removeItem}
          source={props.source}
          keyField={props.keyField}
          onClose={() => setRemoveItem(null)}
        />
      ) : null}
    </Flex>
  )
}
